import Vue from "vue";
import axios from "@/axios";
import i18n, { setTranslationMessages } from "@shared/plugins/i18n";
import {
  formatLangProfileData,
  compareWithCached,
} from "./utils"
import { isJson } from "@shared/components/form-builder/utils";

export const LocalizationStore = {
  namespaced: true,

  state: {
    current_lang_profile: null, // current language profile (Object)
    user_lang_profile_list: [],
    lang_profile_list: [],
    translations: {}, // [key]: Translations
    isCacheLoaded: false,
  },

  getters: {
    getCurrentLangProfile: (state) => state.current_lang_profile,
    getCurrentLangProfileId: (state) => state.current_lang_profile?.language_profile_id,
    getUserLangProfileList: (state) => state.user_lang_profile_list,
    getLangProfileList: (state) => state.lang_profile_list,
    getTranslations: (state) => state.translations,
    getCurrentTranslations: (state) => state.translations[state.current_lang_profile?.language_profile_id] || {},
  },

  mutations: {
    SET_CURRENT_LANG_PROFILE(state, payload) {
      state.current_lang_profile = payload;

      // use lang profile translations:
      i18n.locale = payload?.language_profile_id;
    },
    CHECK_CACHED_LANG(state, currentLangProfile) {
      // add translations cached in the browser storage:
      const {
        profileChanged,
        versionChanged,
        cachedTranslations,
        currentLangProfileId,
      } = compareWithCached(currentLangProfile);

      if (!profileChanged && !versionChanged && cachedTranslations && isJson(cachedTranslations)) {
        const parsedTranslations = JSON.parse(cachedTranslations);

        Vue.set(state.translations, currentLangProfileId, parsedTranslations);
        setTranslationMessages(currentLangProfileId, parsedTranslations);
      }
      state.isCacheLoaded = true;
    },
    CACHE_CURRENT_TRANSLATIONS(_, { lang_profile, data, lang }) {
      // store current lang profile and translations in the browser storage if changed:
      const {
        profileChanged,
        versionChanged,
        cachedTranslations,
      } = compareWithCached(lang_profile);

      if (profileChanged || versionChanged || !cachedTranslations) {
        const translationsString = JSON.stringify(data);

        localStorage.setItem("lang_profile", JSON.stringify(lang_profile));
        localStorage.setItem("lang", lang);
        localStorage.setItem("translations", translationsString);
      }
    },
    SET_USER_LANG_PROFILE_LIST(state, payload = []) {
      state.user_lang_profile_list = payload;
    },
    SET_LANG_PROFILE_LIST(state, payload = []) {
      state.lang_profile_list = payload;
    },
    ADD_LOADED_TRANSLATION(state, { id, data }) {
      Vue.set(state.translations, id, data);

      setTranslationMessages(id, data);
    },
    RESET_TRANSLATIONS(state) {
      // we have reset translations on login
      state.translations = {};
    },
    RESET_CHACHED_TRANSLATIONS() {
      // we have reset translations on login
      localStorage.removeItem("translations");
    },
  },

  actions: {
    resetLangCache({ commit }) {
      commit("RESET_TRANSLATIONS");
      commit("RESET_CHACHED_TRANSLATIONS");
    },
    async fetchUserLangPreference({ commit, dispatch }) {
      const { data } = await axios.get("user/language-preference");
      const userLangPreference = formatLangProfileData(data.language_preference);
      const firstLangProfile = formatLangProfileData(data.language_profile?.[0]);

      commit("SET_USER_LANG_PROFILE_LIST", data.language_profile);

      if (userLangPreference || !firstLangProfile) {
        // load translations from browser cache / server:
        await dispatch("setUserLangProfile", userLangPreference);
      } else if (firstLangProfile) {
        await dispatch("updateUserLangProfile", firstLangProfile);
      }

    },

    async fetchLangProfileTranslations({ commit }, langProfile = {}) {
      const {
        language_profile_id: langProfileId = null,
      } = langProfile;

      const { data } = await axios.get(`localisation/translations/${langProfileId}`);

      const translations = data?.data?.translations

      commit("ADD_LOADED_TRANSLATION", {
        id: langProfileId,
        data: translations,
      });

      return data;
    },

    async updateLangProfileTranslations({ commit }, {id, payload}) {
      const { translations } = payload;
      if (!translations) {
        throw new Error("Translations are required");
      }
      const { data } = await axios.put(`localisation/translations/${id}`, payload);
      // update translations in the store:
      commit("ADD_LOADED_TRANSLATION", {
        id,
        data: translations,
      });

      return data;
    },

    async updateUserLangPreferences(_, langProfileId) {
      const { data } = await axios.put("user/language-preference", {
        language_profile_id: langProfileId,
      });

      return data;
    },

    // update user lang preferences & cache on the FE:
    async setUserLangProfile({ dispatch, commit, state }, langProfile = {}) {
      const {
        language_profile_id: langProfileId = null,
        iso_code,
      } = langProfile || {};
      // run only on first call:
      if (!state.isCacheLoaded) {
        // here, translations are set to i18n if found:
        commit("CHECK_CACHED_LANG", langProfile);
      }

      if (!langProfileId) return;

      // if current lang profile data is already stored in the browser use stored data:
      let langProfileTranslations = state.translations[langProfileId];
      // fetch lang profile translations if isn't stored
      if (!langProfileTranslations) {
        // here, translations are set to i18n:
        const fetchedTranslations = await dispatch("fetchLangProfileTranslations", langProfile);
        langProfileTranslations = fetchedTranslations?.data?.translations;
      }

      commit("SET_CURRENT_LANG_PROFILE", langProfile);

      commit("CACHE_CURRENT_TRANSLATIONS", {
        lang_profile: langProfile,
        data: langProfileTranslations,
        lang: iso_code,
      })
    },

    // update user lang preferences & cache on the BE:
    async updateUserLangProfile({ dispatch }, langProfile = {}) {
      await dispatch("setUserLangProfile", langProfile);
      const {
        language_profile_id: langProfileId = null,
      } = langProfile;

      // store lang preferences on the server:
      const response = await dispatch("updateUserLangPreferences", langProfileId);

      return response;
    },

    /* LANG PROFILES CHANGE */
    async createLangProfile(_, payload) {
      const url = "localisation/profile";

      const { data } = await axios.post(url, payload);

      return data;
    },
    async cloneLangProfile(_, {id, payload = {}}) {
      const url = `localisation/profile/${id}/clone`;

      const { data } = await axios.post(url, payload);

      return data;
    },
    async updateLangProfile(_, {id, payload = {}}) {
      const url = `localisation/profile/${id}`;

      const { data } = await axios.put(url, payload);

      return data;
    },
    async deleteLangProfile(_, langProfileId) {
      const url = `localisation/profile/${langProfileId}`;

      const { data } = await axios.delete(url);

      return data;
    },

    async fetchTranslationKeyChangeHistory(_, { lang_profile_id, key }) {
      const url = `localisation/translations/${lang_profile_id}/log`;

      const { data } = await axios.get(url, {
        params: {
          key,
        }
      });

      return data;
    },

    /* CLIENT LANG PROFILES */
    async fetchLangProfileList({ commit }) {
      const { data } = await axios.get(`localisation/profiles`);
      commit("SET_LANG_PROFILE_LIST", data?.profiles);
      return data;
    },

    async fetchClientLangProfile(_, client_id) {
      const url = `localisation/client/${client_id}/language-profile`;

      const { data } = await axios.get(url);

      return data;
    },
    async updateClientLangProfile(_, {client_id, payload}) {
      const url = `localisation/client/${client_id}/language-profile`;

      const { data } = await axios.post(url, payload);

      return data;
    },
  },
};